// src/components/common/Navbar.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Collapse,
  ListItemIcon,
  Divider,
  Typography,
  ClickAwayListener,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HardwareIcon from "@mui/icons-material/Hardware";
import CodeIcon from "@mui/icons-material/Code";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import WebIcon from "@mui/icons-material/Web";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Logo from "../../assets/logo/logo.png";
import styles from "./Navbar.module.css";

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  const [openCaseStudy, setOpenCaseStudy] = useState(false);
  const [openSoftware, setOpenSoftware] = useState(false);
  const [openServicesDesktop, setOpenServicesDesktop] = useState(false);
  const [openCaseStudyDesktop, setOpenCaseStudyDesktop] = useState(false);
  const [openSoftwareDesktop, setOpenSoftwareDesktop] = useState(false);

  // Navigation Links with Sub-items
  const navLinks = [
    { text: "Home", path: "/" },
    {
      text: "Services",
      path: "/services",
      icon: <SettingsIcon />,
      subItems: [
        {
          text: "Hardware",
          path: "/services/hardware",
          icon: <HardwareIcon fontSize="small" />,
        },
        {
          text: "Software",
          path: "/services/software",
          icon: <CodeIcon fontSize="small" />,
          subItems: [
            {
              text: "Mobile Applications Development",
              path: "/services/software/mobile",
              icon: <PhoneAndroidIcon fontSize="small" />,
            },
            {
              text: "Web Applications Development",
              path: "/services/software/web",
              icon: <WebIcon fontSize="small" />,
            },
          ],
        },
      ],
    },
    {
      text: "Case Study",
      path: "/case-study",
      icon: <SettingsIcon />,
      subItems: [
        {
          text: "Hardware",
          path: "/case-study/hardware",
          icon: <HardwareIcon fontSize="small" />,
        },
        {
          text: "Apps Development / Software Integration",
          path: "/case-study/software-integration",
          icon: <CodeIcon fontSize="small" />,
        },
      ],
    },
    { text: "Partners", path: "/partners" },
    { text: "Internship", path: "/internship" },
    { text: "Innovation", path: "/innovation" },
    { text: "About", path: "/about" },
    { text: "Contact", path: "/contact" },
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleServicesClick = () => {
    setOpenServices(!openServices);
    setOpenCaseStudy(false);
    setOpenSoftware(false);
  };

  const handleCaseStudyClick = () => {
    setOpenCaseStudy(!openCaseStudy);
    setOpenServices(false);
    setOpenSoftware(false);
  };

  const handleSoftwareClick = () => {
    setOpenSoftware(!openSoftware);
  };

  // Desktop click handlers
  const handleServicesDesktopClick = () => {
    setOpenServicesDesktop(!openServicesDesktop);
    setOpenSoftwareDesktop(false);
    setOpenCaseStudyDesktop(false);
  };

  const handleCaseStudyDesktopClick = () => {
    setOpenCaseStudyDesktop(!openCaseStudyDesktop);
    setOpenServicesDesktop(false);
    setOpenSoftwareDesktop(false);
  };

  const handleSoftwareDesktopClick = (e) => {
    e.stopPropagation();
    setOpenSoftwareDesktop(!openSoftwareDesktop);
  };

  const handleClickAway = () => {
    setOpenServicesDesktop(false);
    setOpenSoftwareDesktop(false);
    setOpenCaseStudyDesktop(false);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setOpenServices(false);
    setOpenCaseStudy(false);
    setOpenSoftware(false);
    setOpenServicesDesktop(false);
    setOpenSoftwareDesktop(false);
    setOpenCaseStudyDesktop(false);
  };

  // Desktop Navbar with Click Dropdowns
  const DesktopNav = () => (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box className={styles.desktopNav}>
        {navLinks.map((link) => {
          // If link has subItems, render as dropdown
          if (link.subItems) {
            const isServices = link.text === "Services";
            const isCaseStudy = link.text === "Case Study";
            const isOpen = isServices
              ? openServicesDesktop
              : isCaseStudy
                ? openCaseStudyDesktop
                : false;

            const handleClick = isServices
              ? handleServicesDesktopClick
              : isCaseStudy
                ? handleCaseStudyDesktopClick
                : null;

            return (
              <Box key={link.text} className={styles.dropdownWrapper}>
                <Button
                  className={`${styles.navLink} ${
                    location.pathname.startsWith(link.path) ? styles.active : ""
                  } ${isOpen ? styles.active : ""}`}
                  onClick={handleClick}
                >
                  {link.text}
                  <span className={styles.dropdownIcon}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                </Button>

                {isOpen && (
                  <Box className={styles.dropdownMenu}>
                    {link.subItems.map((subItem) => (
                      <Box
                        key={subItem.text}
                        className={styles.dropdownItemRow}
                      >
                        {subItem.subItems ? (
                          // Software - has sub-items (Mobile/Web)
                          <Box
                            className={`${styles.dropdownItem} ${styles.dropdownItemWithSub} ${
                              openSoftwareDesktop
                                ? styles.activeDropdownItem
                                : ""
                            } ${location.pathname.startsWith(subItem.path) ? styles.activeDropdown : ""}`}
                            onClick={
                              isServices
                                ? handleSoftwareDesktopClick
                                : undefined
                            }
                          >
                            <Box className={styles.dropdownItemIcon}>
                              {subItem.icon}
                            </Box>
                            <Typography variant="body2">
                              {subItem.text}
                            </Typography>
                            {isServices && (
                              <span className={styles.dropdownRightArrow}>
                                {openSoftwareDesktop ? "▼" : "▶"}
                              </span>
                            )}
                          </Box>
                        ) : (
                          // Hardware - no sub-items
                          <Link
                            to={subItem.path}
                            className={`${styles.dropdownItem} ${
                              location.pathname === subItem.path
                                ? styles.activeDropdown
                                : ""
                            }`}
                            onClick={() => {
                              setOpenServicesDesktop(false);
                              setOpenCaseStudyDesktop(false);
                              setOpenSoftwareDesktop(false);
                            }}
                          >
                            <Box className={styles.dropdownItemIcon}>
                              {subItem.icon}
                            </Box>
                            <Typography variant="body2">
                              {subItem.text}
                            </Typography>
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Software Sub-menu - appears as separate div next to Services menu */}
                {openSoftwareDesktop && isServices && (
                  <Box className={styles.softwareSubMenu}>
                    {link.subItems
                      .find((item) => item.text === "Software")
                      .subItems.map((subSubItem) => (
                        <Link
                          key={subSubItem.text}
                          to={subSubItem.path}
                          className={`${styles.dropdownItem} ${styles.subDropdownItem} ${
                            location.pathname === subSubItem.path
                              ? styles.activeDropdown
                              : ""
                          }`}
                          onClick={() => {
                            setOpenServicesDesktop(false);
                            setOpenSoftwareDesktop(false);
                          }}
                        >
                          <Box className={styles.dropdownItemIcon}>
                            {subSubItem.icon}
                          </Box>
                          <Typography variant="body2">
                            {subSubItem.text}
                          </Typography>
                        </Link>
                      ))}
                  </Box>
                )}
              </Box>
            );
          }

          // Regular link
          return (
            <Button
              key={link.text}
              component={Link}
              to={link.path}
              className={`${styles.navLink} ${
                location.pathname === link.path ? styles.active : ""
              }`}
            >
              {link.text}
            </Button>
          );
        })}
        <Button
          variant="contained"
          className={styles.contactButton}
          component={Link}
          to="/contact"
        >
          Get in touch
        </Button>
      </Box>
    </ClickAwayListener>
  );

  // Mobile Drawer
  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      className={styles.drawer}
    >
      <Box className={styles.drawerContent}>
        <Box className={styles.drawerHeader}>
          <img src={Logo} alt="Logo" style={{ width: "100px" }} />
          <IconButton onClick={closeDrawer} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider className={styles.drawerDivider} />

        <List className={styles.drawerList}>
          {navLinks.map((link) => {
            // If link has subItems (Services or Case Study)
            if (link.subItems) {
              const isOpen =
                link.text === "Services" ? openServices : openCaseStudy;
              const handleClick =
                link.text === "Services"
                  ? handleServicesClick
                  : handleCaseStudyClick;

              return (
                <Box key={link.text}>
                  <ListItem
                    button
                    onClick={handleClick}
                    className={`${styles.drawerItem} ${
                      location.pathname.startsWith(link.path)
                        ? styles.activeDrawerItem
                        : ""
                    }`}
                  >
                    <ListItemIcon className={styles.drawerItemIcon}>
                      {link.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={link.text}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>

                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {link.subItems.map((subItem) => (
                        <Box key={subItem.text}>
                          {subItem.subItems ? (
                            // Software - has sub-items (only for Services)
                            link.text === "Services" ? (
                              <>
                                <ListItem
                                  button
                                  onClick={handleSoftwareClick}
                                  className={`${styles.drawerSubItem} ${
                                    location.pathname.startsWith(subItem.path)
                                      ? styles.activeDrawerSubItem
                                      : ""
                                  }`}
                                  sx={{ pl: 4 }}
                                >
                                  <ListItemIcon
                                    className={styles.drawerSubIcon}
                                  >
                                    {subItem.icon}
                                  </ListItemIcon>
                                  <ListItemText primary={subItem.text} />
                                  {openSoftware ? (
                                    <ExpandLess />
                                  ) : (
                                    <ExpandMore />
                                  )}
                                </ListItem>
                                <Collapse
                                  in={openSoftware}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <List component="div" disablePadding>
                                    {subItem.subItems.map((subSubItem) => (
                                      <ListItem
                                        key={subSubItem.text}
                                        button
                                        component={Link}
                                        to={subSubItem.path}
                                        onClick={closeDrawer}
                                        className={`${styles.drawerSubSubItem} ${
                                          location.pathname === subSubItem.path
                                            ? styles.activeDrawerSubItem
                                            : ""
                                        }`}
                                      >
                                        <ListItemIcon
                                          className={styles.drawerSubSubIcon}
                                        >
                                          {subSubItem.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={subSubItem.text}
                                          primaryTypographyProps={{
                                            fontSize: "0.85rem",
                                          }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Collapse>
                              </>
                            ) : (
                              // Case Study - no sub-sub-items
                              <ListItem
                                button
                                component={Link}
                                to={subItem.path}
                                onClick={closeDrawer}
                                className={`${styles.drawerSubItem} ${
                                  location.pathname === subItem.path
                                    ? styles.activeDrawerSubItem
                                    : ""
                                }`}
                                sx={{ pl: 4 }}
                              >
                                <ListItemIcon className={styles.drawerSubIcon}>
                                  {subItem.icon}
                                </ListItemIcon>
                                <ListItemText primary={subItem.text} />
                              </ListItem>
                            )
                          ) : (
                            // Hardware - no sub-items
                            <ListItem
                              button
                              component={Link}
                              to={subItem.path}
                              onClick={closeDrawer}
                              className={`${styles.drawerSubItem} ${
                                location.pathname === subItem.path
                                  ? styles.activeDrawerSubItem
                                  : ""
                              }`}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon className={styles.drawerSubIcon}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText primary={subItem.text} />
                            </ListItem>
                          )}
                        </Box>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            // Regular link
            return (
              <ListItem
                key={link.text}
                button
                component={Link}
                to={link.path}
                onClick={closeDrawer}
                className={`${styles.drawerItem} ${
                  location.pathname === link.path ? styles.activeDrawerItem : ""
                }`}
              >
                <ListItemText
                  primary={link.text}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            );
          })}
        </List>

        <Divider className={styles.drawerDivider} />

        <Button
          variant="contained"
          fullWidth
          className={styles.drawerContactButton}
          component={Link}
          to="/contact"
          onClick={closeDrawer}
          endIcon={<ArrowForwardIcon />}
        >
          Contact
        </Button>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0} className={styles.navbar}>
        <Container maxWidth="xl">
          <Toolbar className={styles.toolbar}>
            <img src={Logo} alt="Logo" style={{ width: "180px" }} />

            {!isMobile ? (
              <DesktopNav />
            ) : (
              <IconButton
                onClick={handleDrawerToggle}
                className={styles.menuButton}
                aria-label="Toggle menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <MobileDrawer />
    </>
  );
};

export default Navbar;
